/**
 * @opsrev/shared — THE CONTRACT
 *
 * Single source of truth shared between:
 *   - apps/capture (Electron desktop recorder)  -> produces SessionBundle, uploads it
 *   - apps/api     (Node processing API)         -> consumes SessionBundle, produces AnalysisResult
 *
 * This file MUST stay self-consistent. Builders import from "@opsrev/shared".
 *
 * Flow: capture -> package SessionBundle -> POST /sessions (multipart) -> API runs
 *       processSession() (step 2a: distillation into IntegrationMap + SOP) -> AnalysisResult.
 *
 * NOTE: "Step 2b" (generating skills/CLIs via a GitHub-triggered claude -p research agent)
 * is OUT OF SCOPE for both apps and is not represented here.
 */
/**
 * IPC channel names — the single source of truth for both the preload bridge
 * and the main-process ipcMain handlers. Main registers `ipcMain.handle` for
 * each invoke channel; the renderer subscribes to event channels via the
 * preload's on/off.
 */
export const IPC_CHANNELS = {
    // invoke (renderer -> main, request/response)
    GET_PERMISSIONS: 'capture:getPermissions',
    REQUEST_PERMISSIONS: 'capture:requestPermissions',
    REQUEST_PERMISSION: 'capture:requestPermission',
    OPEN_PERMISSION_SETTINGS: 'capture:openPermissionSettings',
    RELAUNCH_APP: 'capture:relaunchApp',
    GET_SCREEN_SOURCES: 'capture:getScreenSources',
    START_RECORDING: 'capture:startRecording',
    PAUSE_RECORDING: 'capture:pauseRecording',
    RESUME_RECORDING: 'capture:resumeRecording',
    STOP_RECORDING: 'capture:stopRecording',
    WRITE_CHUNK: 'capture:writeChunk',
    LOG_MARKER: 'capture:logMarker',
    SET_NARRATION: 'capture:setNarration',
    UPLOAD_SESSION: 'capture:uploadSession',
    GET_ENROLLMENT_STATUS: 'capture:getEnrollmentStatus',
    SAVE_ENROLLMENT: 'capture:saveEnrollment',
    // events (main -> renderer, push)
    EVT_STATE: 'capture:evt:state',
    EVT_MARKER: 'capture:evt:marker',
    EVT_EVENT: 'capture:evt:event',
    EVT_ERROR: 'capture:evt:error',
};
/** Maps a BridgeEvent to its push IPC channel. */
export const BRIDGE_EVENT_CHANNELS = {
    state: IPC_CHANNELS.EVT_STATE,
    marker: IPC_CHANNELS.EVT_MARKER,
    event: IPC_CHANNELS.EVT_EVENT,
    error: IPC_CHANNELS.EVT_ERROR,
};
// ---------------------------------------------------------------------------
// HTTP API contract (apps/api)
// ---------------------------------------------------------------------------
/** Multipart field names for POST /sessions. */
export const SESSION_UPLOAD_FIELDS = {
    /** JSON string of SessionMetadata. */
    METADATA: 'metadata',
    /** JSON string of CaptureEvent[]. */
    EVENTS: 'events',
    /** Optional plain-text transcript. */
    TRANSCRIPT: 'transcript',
    /** File: screen video (required). */
    VIDEO: 'video',
    /** File: audio (optional). */
    AUDIO: 'audio',
    /** File: narration (optional). */
    NARRATION: 'narration',
};
/** API route paths. */
export const API_ROUTES = {
    HEALTH: '/health',
    SESSIONS: '/sessions',
    /** Helper to build GET /sessions/:id. */
    session: (id) => `/sessions/${id}`,
};
/** Name of the HS256 admin session cookie. */
export const SESSION_COOKIE_NAME = 'audit_session';
/** Name of the short-lived OIDC handshake cookie (PKCE/state/nonce). */
export const HANDSHAKE_COOKIE_NAME = 'audit_oidc_handshake';
// ---------------------------------------------------------------------------
// Route constant maps (server registers these; SPA + capture consume them)
// ---------------------------------------------------------------------------
/** Device-facing routes (Bearer enrollment-token auth). */
export const DEVICE_ROUTES = {
    REGISTER: '/api/devices/register',
    HEARTBEAT: '/api/devices/heartbeat',
};
/** Human-auth BFF routes (no auth to start the flow; /me reads the cookie). */
export const AUTH_ROUTES = {
    LOGIN: '/api/auth/login',
    CALLBACK: '/api/auth/callback',
    LOGOUT: '/api/auth/logout',
    ME: '/api/auth/me',
};
/** Admin routes (all guarded by requireHeadsetAdmin). */
export const ADMIN_ROUTES = {
    CLIENTS: '/api/admin/clients',
    client: (id) => `/api/admin/clients/${id}`,
    rotateToken: (id) => `/api/admin/clients/${id}/rotate-token`,
    clientExport: (id) => `/api/admin/clients/${id}/export`,
    SESSIONS: '/api/admin/sessions',
    session: (id) => `/api/admin/sessions/${id}`,
    sessionVideo: (id) => `/api/admin/sessions/${id}/video`,
    sessionReprocess: (id) => `/api/admin/sessions/${id}/reprocess`,
    sessionExport: (id) => `/api/admin/sessions/${id}/export`,
};
//# sourceMappingURL=index.js.map